// Galerie produit : défilement tactile natif (scroll-snap), flèches, miniatures, compteur,
// suivi de la variante sélectionnée et zoom plein écran (<dialog>).
if (!customElements.get('mp-galerie')) {
  class MpGalerie extends HTMLElement {
    connectedCallback() {
      this.piste = this.querySelector('[data-mp-piste]');
      this.slides = [...this.querySelectorAll('.mp-galerie__slide')];
      this.miniatures = [...this.querySelectorAll('[data-mp-miniature]')];
      this.compteur = this.querySelector('[data-mp-index]');
      this.dialog = this.querySelector('[data-mp-zoom-dialog]');
      this.indexActif = 0;
      this.mouvementReduit = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.querySelector('[data-mp-precedent]')?.addEventListener('click', () => this.aller(this.indexActif - 1));
      this.querySelector('[data-mp-suivant]')?.addEventListener('click', () => this.aller(this.indexActif + 1));
      this.miniatures.forEach((bouton) =>
        bouton.addEventListener('click', () => this.allerAuMedia(bouton.dataset.mpMiniature))
      );
      this.piste?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.aller(this.indexActif + 1);
        if (event.key === 'ArrowLeft') this.aller(this.indexActif - 1);
      });

      this.observerSlides();
      this.initialiserZoom();

      this.surVariante = (event) => {
        const produit = this.closest('mp-produit');
        if (!produit || event.detail.sectionId !== produit.dataset.section) return;
        // Taille ou couleur : la galerie revient sur la photo de la variante (sans bouger si elle
        // y est déjà)
        if (event.detail.variante?.media) this.allerAuMedia(event.detail.variante.media);
      };
      document.addEventListener('mp:variante:change', this.surVariante);
    }

    disconnectedCallback() {
      document.removeEventListener('mp:variante:change', this.surVariante);
      this.observateur?.disconnect();
    }

    observerSlides() {
      if (!this.piste || this.slides.length < 2) return;
      this.observateur = new IntersectionObserver(
        (entrees) => {
          entrees.forEach((entree) => {
            if (entree.isIntersecting && !this.defilementProgramme) this.marquerActif(this.slides.indexOf(entree.target));
          });
        },
        { root: this.piste, threshold: 0.6 }
      );
      this.slides.forEach((slide) => this.observateur.observe(slide));
    }

    marquerActif(index) {
      if (index < 0 || index === this.indexActif) return;
      this.indexActif = index;
      if (this.compteur) this.compteur.textContent = index + 1;
      // Miniatures (ordinateur) et points (mobile) : repérés par l'identifiant du média
      const mediaId = this.slides[index].dataset.mediaId;
      this.miniatures.forEach((bouton) => bouton.setAttribute('aria-current', String(bouton.dataset.mpMiniature === mediaId)));
      // Met en pause les vidéos qui ne sont plus visibles
      this.slides.forEach((slide, i) => {
        if (i !== index) slide.querySelector('video')?.pause();
      });
    }

    aller(index) {
      if (!this.slides.length) return;
      const cible = (index + this.slides.length) % this.slides.length;
      const gauche = this.slides[cible].offsetLeft;
      this.marquerActif(cible);
      // Déjà sur la bonne photo : aucun défilement (évitait un aller-retour visible)
      if (Math.abs(this.piste.scrollLeft - gauche) < 2) return;

      // Pendant le défilement programmé, l'aimantation (scroll-snap) est coupée et le suivi des
      // photos visibles ignoré : sinon le navigateur ré-aimante sur l'ancienne photo en cours de
      // route et la galerie repasse par la première avant d'arriver à la bonne.
      this.defilementProgramme = true;
      this.piste.style.scrollSnapType = 'none';
      clearTimeout(this.finDefilement);
      const terminer = () => {
        clearTimeout(this.finDefilement);
        this.piste.removeEventListener('scrollend', terminer);
        this.piste.style.scrollSnapType = '';
        this.defilementProgramme = false;
        this.marquerActif(cible);
      };
      this.piste.addEventListener('scrollend', terminer, { once: true });
      // Filet de sécurité pour les navigateurs sans « scrollend »
      this.finDefilement = setTimeout(terminer, this.mouvementReduit ? 50 : 700);
      this.piste.scrollTo({ left: gauche, behavior: this.mouvementReduit ? 'auto' : 'smooth' });
    }

    allerAuMedia(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index >= 0) this.aller(index);
    }

    // Aperçu plein écran : s'ouvre au clic sur la photo (ou sur la loupe), miniatures à gauche,
    // menu à droite (fermer, précédente, suivante), flèches du clavier
    initialiserZoom() {
      if (!this.dialog) return;
      this.cadreZoom = this.dialog.querySelector('[data-mp-zoom-cadre]');
      this.compteurZoom = this.dialog.querySelector('[data-mp-zoom-compteur]');
      const listeMiniatures = this.dialog.querySelector('[data-mp-zoom-miniatures]');

      // Photos zoomables, dans l'ordre de la galerie
      this.photosZoom = this.slides
        .map((slide) => {
          const bouton = slide.querySelector('[data-mp-zoom]');
          if (!bouton) return null;
          const miniature = this.querySelector(`.mp-galerie__miniature[data-mp-miniature="${slide.dataset.mediaId}"] img`);
          return {
            slide,
            bouton,
            src: bouton.dataset.mpZoom,
            alt: bouton.dataset.alt || '',
            vignette: miniature?.currentSrc || miniature?.src || bouton.dataset.mpZoom,
          };
        })
        .filter(Boolean);

      this.boutonsMiniaturesZoom = this.photosZoom.map((photo, index) => {
        const li = document.createElement('li');
        const bouton = document.createElement('button');
        bouton.type = 'button';
        bouton.className = 'mp-apercu__miniature';
        bouton.setAttribute('aria-label', `${index + 1} / ${this.photosZoom.length}`);
        const image = document.createElement('img');
        image.src = photo.vignette;
        image.alt = '';
        image.loading = 'lazy';
        bouton.append(image);
        bouton.addEventListener('click', () => this.afficherZoom(index));
        li.append(bouton);
        listeMiniatures?.append(li);
        return bouton;
      });
      if (this.photosZoom.length < 2) this.dialog.classList.add('mp-galerie__zoom--seule');

      this.photosZoom.forEach((photo, index) => {
        const ouvrir = () => this.ouvrirZoom(index, photo.bouton);
        photo.bouton.addEventListener('click', ouvrir);
        photo.slide.querySelector('img.mp-galerie__image')?.addEventListener('click', ouvrir);
        photo.slide.classList.add('mp-galerie__slide--zoomable');
      });

      this.dialog.querySelector('[data-mp-zoom-precedent]')?.addEventListener('click', () => this.afficherZoom(this.indexZoom - 1));
      this.dialog.querySelector('[data-mp-zoom-suivant]')?.addEventListener('click', () => this.afficherZoom(this.indexZoom + 1));
      this.dialog.querySelector('[data-mp-zoom-fermer]')?.addEventListener('click', () => this.dialog.close());
      // Clic à côté de la photo : fermer
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog || event.target === this.cadreZoom) this.dialog.close();
      });
      this.dialog.addEventListener('keydown', (event) => {
        // Échap : fermeture explicite (la fermeture native peut être bloquée par d'autres scripts)
        if (event.key === 'Escape') {
          event.preventDefault();
          this.dialog.close();
        }
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          this.afficherZoom(this.indexZoom + 1);
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.afficherZoom(this.indexZoom - 1);
        }
      });
      this.dialog.addEventListener('close', () => {
        document.documentElement.style.removeProperty('overflow');
        // Revient sur la photo consultée dans la galerie
        const photo = this.photosZoom[this.indexZoom];
        if (photo) this.allerAuMedia(photo.slide.dataset.mediaId);
        this.declencheurZoom?.focus({ preventScroll: true });
      });
    }

    ouvrirZoom(index, declencheur) {
      this.declencheurZoom = declencheur;
      this.afficherZoom(index);
      document.documentElement.style.overflow = 'hidden';
      this.dialog.showModal();
    }

    afficherZoom(index) {
      const total = this.photosZoom.length;
      if (!total) return;
      this.indexZoom = (index + total) % total;
      const photo = this.photosZoom[this.indexZoom];
      const image = document.createElement('img');
      image.src = photo.src;
      image.alt = photo.alt;
      this.cadreZoom.replaceChildren(image);
      if (this.compteurZoom) this.compteurZoom.textContent = `${this.indexZoom + 1} / ${total}`;
      this.boutonsMiniaturesZoom.forEach((bouton, i) => {
        bouton.setAttribute('aria-current', String(i === this.indexZoom));
        if (i === this.indexZoom) bouton.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
    }
  }

  customElements.define('mp-galerie', MpGalerie);
}
